import {
    strEnum,
    ITCIUserInfo,
    ITCIMarketplaceListing,
    TCIMarketplaceListingStatus,
    TCI_MARKETPLACE_LISTING_VIEW_MODE,
    USER_ROLES,
    TCI_MARKETPLACE_ORG_TYPE,
    USER_SUBSCRIPTION_TRIAL
} from "@tci/common";
import { BoolTableMonad } from "booltable/dist/types";
import { Truth, BoolTable } from "booltable";

//#region - Listing DOM elements catalog
export const MKP_MAIN_ITEMS = strEnum([
    "ICON",
    "VERSION",
    "TAGS",
    "NEW_UPDATE",
    "DATE_CERTIFIED",
    "DATE_UPDATED",
    "PROVIDER_NAME",
    "CATEGORY_ICON",
    "RATING_EDIT",
    "MEDIA",
    "HM_OPTIONS",
    "HM_EDIT_DETAILS",
    "HM_EDIT_UPDATE_VISIBILITY_PUBLIC",
    "HM_EDIT_UPDATE_VISIBILITY_PRIVATE",
    "HM_MANAGE_ACCESS",
    "HM_WITHDRAW_PENDING_LISTING",
    "HM_DELETE_LISTING"
]);
export type MKP_MAIN_ITEMS = keyof typeof MKP_MAIN_ITEMS;

export const MKP_PUBLISH_ITEMS = strEnum([
    "PUBLIC",
    "PRIVATE",
    "PENDING_APPROVAL",
    "ACCESS_REQUEST_COUNT"
]);
export type MKP_PUBLISH_ITEMS = keyof typeof MKP_PUBLISH_ITEMS;

export const MKP_ACTION_ITEMS = strEnum([
    "PRICING_INFO",
    "REQUEST_BUTTON",
    "DELETE_LISTING",
    "GET_BUTTON",
    "UNINSTALL_LINK",
    "INSTALLED_BUTTON",
    "REQUEST_REJECTED_STATUS",
    "ACCESS_REVOKED_STATUS",
    "PENDING_REQUEST"
]);
export type MKP_ACTION_ITEMS = keyof typeof MKP_ACTION_ITEMS;

export const MKP_POPUP = strEnum([
    "GET_ENABLE_SCRIBE",
    "GET_ENABLE_CONNECTOR", // FLOGO
    "GET_ENABLE_APP"
]);
export type MKP_POPUP = keyof typeof MKP_POPUP;

export const MKP_FOOTER_ITEMS = strEnum([
    "PROVIDER_NAME",
    "DESCRIPTION_LABEL_FULL",
    "CATEGORY_ICON",
    "PRICING_INFO",
    "INSTALLED_STATUS",

    // Publish Status items
    "STATUS_PUBLIC",
    "STATUS_PENDING_APPROVAL",
    "STATUS_ACCESS_REQUEST_COUNT",
    "STATUS_PRIVATE",

    "REQUEST_REJECTED_STATUS",
    "ACCESS_REVOKED_STATUS",
    // "PRICING_TOOLTIP",
    "ADDITIONAL_INFO_LINK",
    "MEDIA",
    "EULA_LINK",
    "RATING_VIEW_RO",
]);
export type MKP_FOOTER_ITEMS = keyof typeof MKP_FOOTER_ITEMS;

export const MKP_SECTION_ITEMS = strEnum([
    ...Object.values(MKP_MAIN_ITEMS),
    ...Object.values(MKP_PUBLISH_ITEMS),
    ...Object.values(MKP_ACTION_ITEMS),
    ...Object.values(MKP_FOOTER_ITEMS),
    ...Object.values(MKP_POPUP)
]);
export type MKP_SECTION_ITEMS = keyof typeof MKP_FOOTER_ITEMS;
////#endregion

export interface IMkpV2ListEvalRes {
    visible: boolean;
    enabled?: boolean;
    isDownloadable?: boolean;
}

export interface IMkpV2ListContext {
    item: MKP_MAIN_ITEMS | MKP_ACTION_ITEMS | MKP_PUBLISH_ITEMS | MKP_FOOTER_ITEMS | MKP_POPUP;
    asset: ITCIMarketplaceListing;
    mode: TCI_MARKETPLACE_LISTING_VIEW_MODE;
    userInfo: ITCIUserInfo;
}

// Function for forming a valid output object from retreived results
export const formatResult = (objs: IMkpV2ListEvalRes[]): IMkpV2ListEvalRes => ({
    visible: objs.some(obj => !!obj.visible),
    enabled: objs.some(obj => !!obj.enabled)
});

//#region - Conditions catalog
export const CONDITION_MODE = strEnum([
    "IS_LIST_CARD",
    "IS_DETAIL",
    "IS_PREVIEW_CARD",
    "IS_PREVIEW_DETAIL"
]);
export const CONDITION_IS_ALLOWED_IN = strEnum([
    "LIST_CARD",
    "DETAIL",
    "PREVIEW_CARD",
    "PREVIEW_DETAIL"
]);
export const CONDITION_ORG = strEnum([
    "ORG_IS_NONE",
    "ORG_IS_PROVIDER",
    "ORG_IS_LISTING",
    "ORG_IS_SUBSCRIBER"
]);
export const CONDITION_STATUS_MANAGING = strEnum([
    "IS_PRIVATELY_AVAILABLE",
    "IS_PENDING_APPROVAL",
    "IS_PUBLICLY_AVAILABLE"
]);
export const CONDITION_STATUS_CONSUMING = strEnum([
    "IS_LISTING_REQUEST",
    "IS_PENDING_REQUEST",
    "IS_REQUEST_APPROVED",
    "IS_REQUEST_REJECTED",
    "IS_LISTING_DISABLED",
    "IS_LISTING_ENABLED",
    "IS_LISTING_GET",
    "IS_ACCESS_REVOKED",
    "IS_REVOKE_AKNOWLEDGED"
]);
//#endregion

//#region - Conditions catalog binding condition string with the actual evaluation

/**
 * Main check for determining if current user is manager of the listing
 */

export const isSameOrg = (context: IMkpV2ListContext): boolean => {
    return context.userInfo.gsbc === context.asset.managingOrgId; // listing belongs to user Org
};

export const isRO = (context: IMkpV2ListContext): boolean => {
    return context.userInfo.rolX[0] === USER_ROLES.RO;
};

export const isTrial = (context: IMkpV2ListContext): boolean => {
    return context.userInfo.st === USER_SUBSCRIPTION_TRIAL; // user is a trial user
};

export const isOrgUserNotRO = (context: IMkpV2ListContext): boolean => {
    const isValidRole = [USER_ROLES.USR, USER_ROLES.ADM].includes(context.userInfo.rolX[0] as any);
    return isSameOrg(context) && !isTrial(context) && isValidRole;
};

export const isAdmin = (context: IMkpV2ListContext): boolean => {
    return !isTrial(context) && context.userInfo.rolX[0] === USER_ROLES.ADM;
};

// IPAS-17225 - User can be Admin but does not mean it's manager of listings
export const isAdminOfSameOrg = (context: IMkpV2ListContext): boolean => {
    return isSameOrg(context) && isAdmin(context);
};

export const ConditionsForStatusManaging = (context: IMkpV2ListContext): BoolTableMonad => {
    if (typeof context.userInfo.rolX === "undefined" || typeof context.userInfo.rolX[0] === "undefined") {
        context.userInfo = Object.assign(context.userInfo, { rolX: [null] });
    }

    return BoolTable.of([
        [
            CONDITION_STATUS_MANAGING.IS_PRIVATELY_AVAILABLE,
            Truth.of([
                (context.asset.listingStatus[context.userInfo.gsbc] || [])
                    .map(st => st.toString())
                    .includes(TCIMarketplaceListingStatus.PRIVATELY_AVAILABLE.toString())
            ]).and()
        ],
        [
            CONDITION_STATUS_MANAGING.IS_PENDING_APPROVAL,
            Truth.of([
                (context.asset.listingStatus[context.userInfo.gsbc] || [])
                    .map(st => st.toString())
                    .includes(TCIMarketplaceListingStatus.PENDING_APPROVAL.toString())
            ]).and()
        ],
        [
            CONDITION_STATUS_MANAGING.IS_PUBLICLY_AVAILABLE,
            Truth.of([
                (context.asset.listingStatus[context.userInfo.gsbc] || [])
                    .map(st => st.toString())
                    .includes(TCIMarketplaceListingStatus.PUBLICLY_AVAILABLE.toString())
            ]).and()
        ]
    ]);
};

export const ConditionsForStatusConsuming = (context: IMkpV2ListContext): BoolTableMonad =>
    BoolTable.of([
        [
            CONDITION_STATUS_CONSUMING.IS_PENDING_REQUEST,
            Truth.of([
                Object.keys(context.asset.listingStatus).includes(context.userInfo.gsbc.toString()),
                (context.asset.listingStatus[context.userInfo.gsbc.toString()] || [])
                    .map(st => st.toString())
                    .includes(TCIMarketplaceListingStatus.PENDING_REQUEST.toString())
            ]).and()
        ],
        [
            CONDITION_STATUS_CONSUMING.IS_LISTING_REQUEST,
            Truth.of([
                Object.keys(context.asset.listingStatus).includes(context.userInfo.gsbc.toString()),
                (context.asset.listingStatus[context.userInfo.gsbc.toString()] || [])
                    .map(st => st.toString())
                    .includes(TCIMarketplaceListingStatus.LISTING_REQUEST.toString())
            ]).and()
        ],
        [
            CONDITION_STATUS_CONSUMING.IS_REQUEST_APPROVED,
            Truth.of([
                Object.keys(context.asset.listingStatus).includes(context.userInfo.gsbc.toString()),
                (context.asset.listingStatus[context.userInfo.gsbc.toString()] || [])
                    .map(st => st.toString())
                    .includes(TCIMarketplaceListingStatus.REQUEST_APPROVED.toString())
            ]).and()
        ],
        [
            CONDITION_STATUS_CONSUMING.IS_REQUEST_REJECTED,
            Truth.of([
                Object.keys(context.asset.listingStatus).includes(context.userInfo.gsbc.toString()),
                (context.asset.listingStatus[context.userInfo.gsbc.toString()] || [])
                    .map(st => st.toString())
                    .includes(TCIMarketplaceListingStatus.REQUEST_REJECTED.toString())
            ]).and()
        ],
        [
            CONDITION_STATUS_CONSUMING.IS_LISTING_DISABLED,
            Truth.of([
                Object.keys(context.asset.listingStatus).includes(context.userInfo.gsbc.toString()),
                (context.asset.listingStatus[context.userInfo.gsbc.toString()] || [])
                    .map(st => st.toString())
                    .includes(TCIMarketplaceListingStatus.LISTING_DISABLED.toString())
            ]).and()
        ],
        [
            CONDITION_STATUS_CONSUMING.IS_LISTING_ENABLED,
            Truth.of([
                Object.keys(context.asset.listingStatus).includes(context.userInfo.gsbc.toString()),
                (context.asset.listingStatus[context.userInfo.gsbc.toString()] || [])
                    .map(st => st.toString())
                    .includes(TCIMarketplaceListingStatus.LISTING_ENABLED.toString())
            ]).and()
        ],
        [
            CONDITION_STATUS_CONSUMING.IS_LISTING_GET,
            Truth.of([
                Object.keys(context.asset.listingStatus).includes(context.userInfo.gsbc.toString()),
                (context.asset.listingStatus[context.userInfo.gsbc.toString()] || [])
                    .map(st => st.toString())
                    .includes(TCIMarketplaceListingStatus.LISTING_GET.toString())
            ]).and()
        ],
        [
            CONDITION_STATUS_CONSUMING.IS_ACCESS_REVOKED,
            Truth.of([
                Object.keys(context.asset.listingStatus).includes(context.userInfo.gsbc.toString()),
                (context.asset.listingStatus[context.userInfo.gsbc.toString()] || [])
                    .map(st => st.toString())
                    .includes(TCIMarketplaceListingStatus.ACCESS_REVOKED.toString())
            ]).and()
        ],
        [
            CONDITION_STATUS_CONSUMING.IS_REVOKE_AKNOWLEDGED,
            Truth.of([
                Object.keys(context.asset.listingStatus).includes(context.userInfo.gsbc.toString()),
                (context.asset.listingStatus[context.userInfo.gsbc.toString()] || [])
                    .map(st => st.toString())
                    .includes(TCIMarketplaceListingStatus.ACCESS_REVOKE_ACKNOWLEDGED.toString())
            ]).and()
        ]
    ]);
//#endregion
